import { FunctionComponent, useMemo } from "react";
import styles from "./styling/OccupancyUtilisationTable.module.scss";

import { Box, Skeleton, SkeletonText, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";

import { useOccupancyContext } from "@tether-web-portal/contexts/OccupancyContext";
import { Typography } from "@tether-web-portal/components/common";
import {
  OccupancyFilterType,
  OccupancyUtilisationRowType,
  OccupancyUtilisationSpaceChildType,
} from "@tether-web-portal/web-workers/occupancy";

import { OccupancyUtilisationTableRow } from "./OccupancyUtilisationTableRow";

export const OccupancyUtilisationTable: FunctionComponent = () => {
  const { isLiveView, utilisationTableData, utilisationLoading, pageFilters, tilesData } =
    useOccupancyContext();
  const unconfiguredRooms = tilesData?.overall?.unconfiguredRooms ?? [];

  const filteredTableData = useMemo(() => {
    if (!pageFilters?.spaceType) {
      return utilisationTableData;
    } else if (pageFilters.spaceType === OccupancyFilterType.FLOOR) {
      return utilisationTableData.filter((x) => x.rowType !== OccupancyUtilisationRowType.BUILDING);
    } else {
      return utilisationTableData.filter((x) => x.rowType === OccupancyUtilisationRowType.ROOM);
    }
  }, [utilisationTableData, pageFilters]);

  const { showTotalInOut, showDwellTime } = useMemo(() => {
    const showTotalInOut =
      !isLiveView &&
      utilisationTableData.some((row) =>
        row.spaceChildren?.some((x) => x.childType === OccupancyUtilisationSpaceChildType.LINE)
      );
    const showDwellTime = utilisationTableData.some((row) =>
      row.spaceChildren?.some((x) => x.childType === OccupancyUtilisationSpaceChildType.REGION)
    );

    return {
      showTotalInOut,
      showDwellTime,
    };
  }, [utilisationTableData, isLiveView]);

  const showUsageTime = !isLiveView;

  const tableHeaders = useMemo(() => {
    const updatedHeaders: { label: string; width?: number }[] = [
      { label: "Type" },
      { label: "Area", width: 10000 },
      { label: "Usage" },
      { label: "Occupancy" },
    ];

    if (isLiveView) {
      updatedHeaders.push(...[{ label: "People In" }, { label: "People Out" }, { label: "Foot-Traffic" }]);
    } else {
      updatedHeaders.push(...[{ label: "Min Occupancy" }, { label: "Max Occupancy" }, { label: "Avg Occupancy" }]);
    }

    if (showDwellTime) {
      updatedHeaders.push(...[{ label: "Min Dwell" }, { label: "Max Dwell" }, { label: "Avg Dwell" }]);
    }

    if (showUsageTime) {
      updatedHeaders.push({ label: "Total Usage" });
    }

    if (showTotalInOut) {
      updatedHeaders.push(...[{ label: "Total In" }, { label: "Total Out" }]);
    }

    updatedHeaders.push({ label: "Devices" });

    return updatedHeaders;
  }, [isLiveView, showDwellTime, showTotalInOut, showUsageTime]);

  // Wrap the table in a horizontally-scrolling box so a wide table can never
  // push the page itself sideways. The "Area" column requests width=10000
  // (a hint to take all available space), so the table happily grows past
  // the page on narrow viewports. The wrapper pins itself to its parent's
  // width (`width="100%"`) and the table scrolls inside.
  //
  // `minWidth={0}` is the flex-item escape hatch — the dashboard's parent
  // `<Stack>` is a flex container, and its default `min-width: auto`
  // would let a child grow to fit content (the wide table) and drag the
  // ancestors with it. Setting it to 0 lets the Box honour `width: 100%`
  // even when the table inside is wider.
  return (
    <Box width="100%" minWidth={0} overflowX="auto">
      <Table className={styles.occupancyUtilisationTable}>
        <Thead>
        <Tr>
          {tableHeaders.map((header) => (
            <Th key={header.label} width={header.width}>
              {header.label}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {!utilisationLoading && filteredTableData.length < 1 ? (
          <Tr>
            <Td colSpan={tableHeaders.length} paddingY={10}>
              <Typography textAlign="center" size="lg" fontStyle="italic" color="gray">
                There are no spaces matching your filters...
              </Typography>
            </Td>
          </Tr>
        ) : utilisationLoading ? (
          Array.from({ length: 8 }).map((_, rowIndex) => (
            <Tr key={rowIndex}>
              {Array.from({ length: tableHeaders.length }).map((_, columnIndex) => (
                <Td key={columnIndex}>
                  <Skeleton height={23}>
                    <SkeletonText noOfLines={1} />
                  </Skeleton>
                </Td>
              ))}
            </Tr>
          ))
        ) : (
          filteredTableData.map((row) => (
            <OccupancyUtilisationTableRow
              key={row.spaceId ?? row.spaceName}
              row={row}
              isLiveView={isLiveView}
              showDwellTime={showDwellTime}
              showTotalInOut={showTotalInOut}
              showUsageTime={showUsageTime}
              unconfiguredRooms={unconfiguredRooms}
            />
          ))
        )}
        </Tbody>
      </Table>
    </Box>
  );
};
